import { HandleInvoiceTransitionArgs } from 'pages/invoices/InvoiceWorkflow';
import {
    mapInvoiceMatterDetailsToInvoiceMatterForm,
    mapInvoiceMatterFormDataToAdjustmentDto,
} from 'pages/invoices/util/InvoicesPageUtil';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import {
    useGetInvoiceMatterSummaryQuery,
    useUpdateInvoiceMatterAdjustmentsMutation,
    useUpdateInvoiceMatterFeeArrangementTypeMutation,
    useUpdateInvoiceMatterStatusMutation,
} from 'store/api/invoice-details';
import { ClassifierData } from 'types/classifiers';
import { InvoiceMatterForm } from 'types/invoice-details';
import { InvoiceData, InvoiceMatterDetailsDto } from 'types/invoices';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { useTimecardPrecision } from 'utils/useTimecardPrecision';

interface Args {
    invoice: InvoiceData;
    invoiceMatter: InvoiceMatterDetailsDto;
    classifiers: Record<string, ClassifierData>;
    handleStatusLoadingState: (isLoading: boolean) => void;
    /** The case's card is open in edit: a refetched summary waits, so it does not overwrite the changes before Save. */
    isEditing?: boolean;
}

/**
 * One case of an invoice: its summary, the form its adjustments and its timecard and expense tables edit, and the
 * saves behind them. Each place that shows the case calls it, so the case card and the case's item tables need not
 * share a parent; the summary is one cached query whichever of them asks.
 */
export const useInvoiceMatterForm = ({
    invoice,
    invoiceMatter,
    classifiers,
    handleStatusLoadingState,
    isEditing = false,
}: Args) => {
    const { isTwoDecimalEntryEnabled } = useTimecardPrecision();

    const {
        data: summaryData,
        isLoading: isMatterSummaryLoading,
        isFetching: isMatterSummaryFetching,
    } = useGetInvoiceMatterSummaryQuery({
        invoiceId: invoice.id,
        matterId: invoiceMatter.matterId,
    });

    const isDisplayedInPC = !!invoice.isDisplayedInPaymentCurrency;

    // Derived from RTK Query summary + props; computing during render avoids a
    // useState/useEffect mirror.
    const initialData = useMemo<InvoiceMatterForm | undefined>(
        () =>
            summaryData
                ? mapInvoiceMatterDetailsToInvoiceMatterForm(
                      classifiers,
                      invoiceMatter,
                      summaryData,
                      isTwoDecimalEntryEnabled,
                      isDisplayedInPC
                  )
                : undefined,
        [classifiers, invoiceMatter, summaryData, isTwoDecimalEntryEnabled, isDisplayedInPC]
    );

    const form = useForm<InvoiceMatterForm>({
        defaultValues: {
            matterId: invoiceMatter.matterId,
            discountRate: null,
            adjustmentAmount: null,
            invoiceExpenses: [],
            invoiceTimecards: [],
            invoiceAssignments: [],
            adjustmentAmountForFees: null,
            adjustmentAmountForExpensesWithoutVat: null,
            adjustmentAmountForExpensesWithVat: null,
            capRemainingBeforeInvoice: null,
            areCostsCapped: false,
            areCostsDiscounted: false,
        },
        // `undefined` skips the sync; it runs again once the edit closes.
        values: isEditing ? undefined : initialData,
    });
    const { handleSubmit } = form;

    const [updateInvoiceMatterAdjustments] = useUpdateInvoiceMatterAdjustmentsMutation();
    const [updateInvoiceMatterStatus, { isLoading: isMatterStatusUpdateLoading }] =
        useUpdateInvoiceMatterStatusMutation();
    const [updateInvoiceMatterFeeArrangementType, { isLoading: isMatterFeeArrengementTypeUpdateLoading }] =
        useUpdateInvoiceMatterFeeArrangementTypeMutation();

    // TODO: react-doctor/no-prop-callback-in-effect — propagating RTK Query mutation
    // loading flags to the parent. Requires lifting the mutations or using a context;
    // safe alternative not obvious here.
    useEffect(() => {
        handleStatusLoadingState(isMatterStatusUpdateLoading || isMatterFeeArrengementTypeUpdateLoading);
    }, [handleStatusLoadingState, isMatterStatusUpdateLoading, isMatterFeeArrengementTypeUpdateLoading]);

    const handleStatusChange = useCallback(
        async (args: HandleInvoiceTransitionArgs) => {
            await updateInvoiceMatterStatus({
                invoiceId: invoice.id,
                matterId: invoiceMatter.matterId,
                invoiceStatusTypeCode: args.to,
                transitionActionTypeCode: args.transactionActionType,
                transitionComment: args.comment,
            })
                .unwrap()
                .catch(console.error);
        },
        [invoice.id, invoiceMatter.matterId, updateInvoiceMatterStatus]
    );

    const handleUpdateFeeArrangementType = useCallback(async () => {
        await updateInvoiceMatterFeeArrangementType({
            invoiceId: invoice.id,
            matterId: invoiceMatter.matterId,
        })
            .unwrap()
            .catch(console.error);
    }, [invoice.id, invoiceMatter.matterId, updateInvoiceMatterFeeArrangementType]);

    const onSubmitAdjustment = async (formData: InvoiceMatterForm): Promise<void> => {
        try {
            const data = mapInvoiceMatterFormDataToAdjustmentDto(formData, invoice.id);
            await updateInvoiceMatterAdjustments({ ...data })
                .unwrap()
                .catch(console.error);
        } catch (e) {
            console.error('onSubmitAdjustment error: ', e);
            errorAlert(t('invoices.error-submitting-adjustment'));
        }
    };

    const handleAdjustInvoiceMatter = () => {
        handleSubmit((data) => onSubmitAdjustment(data))();
    };

    return {
        form,
        summaryData,
        initialData,
        isMatterSummaryLoading,
        isMatterSummaryFetching,
        handleStatusChange,
        handleUpdateFeeArrangementType,
        handleAdjustInvoiceMatter,
    };
};
